import { useCallback, useEffect, useRef, useState } from "react";
import { useMount } from "react-use";

import { useResizeObserver } from "fond/utils/hooks";
import localStorage from "fond/utils/localStorage";
import { DEFAULT_TOPBAR_HEIGHT } from "fond/widgets/PageContent/constants";

const STORAGE_KEY = "FOND_TOPNAV_SLOT_HEIGHT";

interface UseTopNavHeightReturn {
  /**
   * Ref to attach to the top navigation container
   */
  topnavRef: React.RefObject<HTMLDivElement>;
  /**
   * Current height of the top navigation in pixels
   */
  topNavHeight: number;
}

/**
 * Custom hook to manage the top navigation height.
 * Handles dynamic height calculation based on content (e.g., banners),
 * persists height to localStorage, and responds to resize and banner change events.
 *
 * @returns An object containing:
 * - `topnavRef`: Ref to attach to the top navigation container
 * - `topNavHeight`: Current height of the top navigation in pixels
 */
export const useTopNavHeight = (): UseTopNavHeightReturn => {
  const topnavRef = useRef<HTMLDivElement>(null);
  const storedHeight = parseInt(localStorage.getItem(STORAGE_KEY, DEFAULT_TOPBAR_HEIGHT), 10);
  const initialHeight = Number.isFinite(storedHeight) ? storedHeight : DEFAULT_TOPBAR_HEIGHT;
  const [topNavHeight, setTopNavHeight] = useState<number>(initialHeight);

  const updateTopNavHeight = useCallback(() => {
    const height = topnavRef.current?.clientHeight ?? 0;
    setTopNavHeight(height);
    localStorage.setItem(STORAGE_KEY, height.toString());
  }, []);

  /**
   * Determines the required height for the toolbar based on any conditional content
   * such as the impersonating banner or other notification banners.
   */
  useResizeObserver(topnavRef, updateTopNavHeight);

  // make sure the initial height is set
  useMount(updateTopNavHeight);

  // Listen for banner changes and force a remeasurement
  // This handles async banner loading that ResizeObserver might miss
  useEffect(() => {
    const handleBannerChange = () => {
      updateTopNavHeight();
    };
    window.addEventListener("topbar-banners-changed", handleBannerChange);
    return () => {
      window.removeEventListener("topbar-banners-changed", handleBannerChange);
    };
  }, [updateTopNavHeight]);

  return { topnavRef, topNavHeight };
};
